import { useMutation } from '@apollo/client'
import { useFormik } from 'formik'
import { useCallback } from 'react'
import { bool, object, string } from 'yup'

import { COUNTRIES } from 'constants/addresses'
import { RegistrationShortMutation, RegistrationShortMutationVariables } from 'gql'
import { EMAIL } from 'constants/schema'
import { useDataLayer, useQueryParams } from 'hooks'
import { USER_BASE_DATA } from 'gql/user/queries'
import { REGISTRATION_SHORT } from 'gql/user/mutations'
import { PARTNER_SLUG } from 'constants/constant'
import SignUpFormComponent from './component'

const validationSchema = object().shape({
  newsletter: bool(),
  firstName: string(),
  lastName: string(),
  terms: bool().oneOf([true], 'You must accept the Terms and Conditions'),
  email: EMAIL(),
  password: string()
    .min(6, 'Password must be longer than 6 characters!')
    .required('Password is required!'),
})

const initialValues = {
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  country: COUNTRIES[0].value,
  terms: true,
  newsletter: false,
}

const SignUpFormShort = ({ onFinish, logInQuery, message }) => {
  const { paths, withQueryParams } = useQueryParams()
  const dl = useDataLayer()

  const [mutate, { loading }] = useMutation<
    RegistrationShortMutation,
    RegistrationShortMutationVariables
  >(REGISTRATION_SHORT, {
    refetchQueries: [USER_BASE_DATA],
  })

  const onSubmit = useCallback(
    (values, { setStatus }) => {
      mutate({ variables: { ...values, partnerSlug: PARTNER_SLUG } }).then((res) => {
        if (res.data.signup.__typename === 'SignupFailure') {
          setStatus(
            res.data.signup.errorMessage || 'An error occurred while signing up. Please try again.'
          )

          return
        }

        dl.push({
          event: 'sign_up',
          method: 'email-pass',
        })

        onFinish?.()
      })
    },
    [dl, mutate, paths.HOME, onFinish]
  )

  const { handleSubmit, handleChange, handleBlur, values, errors, status, touched } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  })

  return (
    <SignUpFormComponent
      status={status}
      values={values}
      errors={errors}
      title={message}
      touched={touched}
      loading={loading}
      handleBlur={handleBlur}
      handleSubmit={handleSubmit}
      handleChange={handleChange}
      logInUrl={withQueryParams(paths.LOGIN, { ...logInQuery })}
    />
  )
}

export default SignUpFormShort
